import $ from 'jquery';
import GeoJSON from 'ol/format/GeoJSON';

import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { logger } from 'cadenza/utils/logging';
import { ajax, defaultXhrErrorHandler } from 'cadenza/api-client/api';
import { MIMETYPE_DISY_POST_MESSAGE, MIMETYPE_DISY_SELECTED_FEATURES } from 'cadenza/api-client/api-constants';
import { MIMETYPE_GEOJSON, MIMETYPE_JSON } from 'cadenza/model/media-type';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { LegendItemDto } from 'cadenza/map/dto/LegendItemDto';
import type { FeatureDto } from 'cadenza/map/dto/feature-dto';
import type { FeatureSelectionDto } from 'cadenza/map/dto/feature-selection-dto';
import { supportsTouch } from 'cadenza/utils/media';

import type { FeatureCollection } from 'geojson';
import type { Geometry } from 'ol/geom';

const LAYER_ID = '{LAYER_ID}';
const DEFAULT_SELECTION_URL = `/layers/${LAYER_ID}/selection`;

export class MapsApi {

  _mapId: string;
  _selectionUrl: string;
  _featureInfoUrl: string;

  constructor (
    { mapId, selectionUrl = DEFAULT_SELECTION_URL, featureInfoUrl = '/map' }:
    { mapId: string; selectionUrl: string; featureInfoUrl: string }) {
    this._mapId = mapId;
    this._selectionUrl = selectionUrl;
    this._featureInfoUrl = featureInfoUrl;
  }

  getSelectedLayerFeaturesIds (layerId: string, mapScale: DimensionDto) {
    const data = { mapScale, selected: true, ids: true };
    const headers = { Accept: MIMETYPE_DISY_SELECTED_FEATURES };
    return ajax<FeatureSelectionDto>(`/layers/${layerId}/features`, { data, headers });
  }

  selectFeatures (
    selectedLayerId: string,
    geometry: Geometry,
    mapSize: DimensionDto,
    mapExtent: WorldBoxDto,
    modifiers: Modifiers,
    mapScale: number) {
    const jsonGeometry = new GeoJSON().writeGeometry(geometry);
    const contentType = MIMETYPE_GEOJSON;
    return selectFeatures({ url: this._selectionUrl, selectedLayerId, data: jsonGeometry, mapSize, mapExtent, modifiers, mapScale, contentType });
  }

  getFeatureInfo (featureInfoArgs: GetFeatureInfoOptions) {
    return Promise.resolve(
      $.ajax(cadenzaUrl(this._featureInfoUrl), {
        method: 'GET',
        data: getFeatureInfoParams(featureInfoArgs),
        dataType: 'text'
      }).catch(
        it => defaultXhrErrorHandler(it, () => logger.error('Error getting feature info'))
      ));
  }

}

export function getSelectedLayerFeaturesAsPostMessage (layerId: string, srsCode: string, mapScale: number) {
  return getSelectedLayerFeatures(layerId, srsCode, mapScale, MIMETYPE_DISY_POST_MESSAGE);
}

export function getSelectedLayerFeatures (layerId: string, srsCode: string, mapScale: number, accept = MIMETYPE_DISY_SELECTED_FEATURES) {
  const data = { selected: true, srsCode, mapScale };
  const headers = { Accept: accept };
  return ajax<FeatureCollection>(`/layers/${layerId}/features`, { data, headers });
}

export function getSelectedLayerFeaturesByGeometry (
  layerId: string,
  mapSize: DimensionDto,
  mapExtent: WorldBoxDto,
  geometry: Geometry,
  srsCode: string,
  mapScale: number) {
  const data = { geometry: new GeoJSON().writeGeometry(geometry), mapScale };
  const headers = { Accept: MIMETYPE_DISY_SELECTED_FEATURES };
  return ajax<FeatureCollection>(`/layers/${layerId}/features?mapSize=${mapSize}&mapExtent=${mapExtent}&srsCode=${srsCode}`, { data, headers });
}

export function getLayerFeatureAttributes (layerId: string) {
  return ajax(`/layers/${layerId}/attributesMetaData`, {
    method: 'GET'
  });
}

export function getLayerFeatureAttributeValues (layerId: string, featureIds: string[]) {
  if (Array.isArray(featureIds)) {
    const url = `/layers/${layerId}/features?view=edit`;
    return ajax(url, {
      method: 'POST',
      data: JSON.stringify(featureIds),
      contentType: MIMETYPE_JSON
    });
  } else {
    const url = `/layers/${layerId}/features/${featureIds}?view=edit`;
    return ajax(url, {
      method: 'GET'
    });
  }
}

export function saveFeature (layerId: string, featureId: string, featureDto: FeatureDto) {
  const url = `/layers/${layerId}/features/${featureId}`;
  return ajax(url, {
    type: 'PUT',
    data: JSON.stringify(featureDto),
    contentType: MIMETYPE_JSON
  });
}

interface BulkUpdateFeatureDto {
  /** features to update */
  featureIds: string[];
  /**
   * contains modifications to attribute
   * - attribute absent: don't change, use old value
   * - attribute present, but null: write null value on non-mandatory attribute
   * attribute present: use new value
   */
  modifications: Record<string, object>;
}

export function saveFeatures (layerId: string, bulkUpdateFeatureDto: BulkUpdateFeatureDto) {
  const url = `/layers/${layerId}/features`;
  return ajax(url, {
    type: 'PATCH',
    data: JSON.stringify(bulkUpdateFeatureDto),
    contentType: MIMETYPE_JSON
  });
}

export function createFeature (layerId: string, featureDto: FeatureDto) {
  const url = `/layers/${layerId}/features`;
  return ajax(url, {
    type: 'POST',
    data: JSON.stringify(featureDto),
    contentType: MIMETYPE_JSON
  });
}

export function getLayerExtent (layerId: string, srsCode: string) {
  return ajax(`/layers/${layerId}/extent`, { data: { srsCode } });
}

export function removeSelectedFeatures (layerId: string) {
  return ajax(`/layers/${layerId}/features?selected`, { type: 'DELETE' });
}

export function deselectFeatures (visibleFeatureLayerIDsWithSelectedFeatures: string[]) {
  const url = `/layers;layerIds=${visibleFeatureLayerIDsWithSelectedFeatures.join()}/selection`;
  return ajax(url, {
    type: 'DELETE'
  });
}

export function selectFeaturesById ({ layerId, featureIds }: { layerId: string; featureIds: string[] }) {
  const featureIdsAsJSON = JSON.stringify(featureIds);
  const contentType = MIMETYPE_JSON;
  const url = DEFAULT_SELECTION_URL.replace(LAYER_ID, layerId);

  return ajax(url, {
    method: 'POST',
    data: featureIdsAsJSON,
    contentType
  });
}

export function selectIntegrationFeaturesById ({ layerId, featureIds }: { layerId: string; featureIds: string[] }) {
  const contentType = MIMETYPE_DISY_POST_MESSAGE;
  const url = DEFAULT_SELECTION_URL.replace(LAYER_ID, layerId);
  return ajax(url, {
    method: 'POST',
    data: JSON.stringify(featureIds),
    contentType
  });
}

export function getLegendStructure (
  { layerId, mapSize, extent, srsCode }: { layerId: string; mapSize: DimensionDto; extent: WorldBoxDto; srsCode: string },
  signal?: AbortSignal) {
  return ajax<LegendItemDto[]>('/legend', {
    signal,
    headers: { Accept: MIMETYPE_JSON },
    data: {
      'LAYERS': encodeURIComponent(layerId),
      'WIDTH': mapSize[0],
      'HEIGHT': mapSize[1],
      'BBOX': extent.join(),
      'SRS': srsCode
    }
  });
}

interface GetFeatureInfoOptions {
  mapId: string;
  layersIDs: string[];
  selectedLayerID: string;
  scriptedLayersIDs: string[];
  projection: string;
  mapSize: DimensionDto;
  mapExtent: WorldBoxDto;
  clickPosition: DimensionDto;
  maxFeatures: number;
  version: string;
  format: string;
  infoFormat: string;
  styles: string[];
}

function getFeatureInfoParams ({ mapId, layersIDs, selectedLayerID, scriptedLayersIDs, projection, mapSize, mapExtent, clickPosition, maxFeatures, version, format, infoFormat, styles }: GetFeatureInfoOptions) {
  const baseParams = {
    MAP_ID: mapId,
    SERVICE: 'WMS',
    VERSION: version,
    REQUEST: 'GetFeatureInfo',
    EXCEPTIONS: MIMETYPE_JSON, // we want to get error reports in JSON format for feature info requests
    BBOX: mapExtent.toString(),
    FEATURE_COUNT: maxFeatures,
    WIDTH: mapSize[0],
    HEIGHT: mapSize[1],
    FORMAT: format,
    INFO_FORMAT: infoFormat,
    STYLES: styles.toString(),
    SELECTED_LAYERS: selectedLayerID,
    LAYERS: layersIDs.toString(),
    QUERY_LAYERS: layersIDs.toString(),
    SCRIPTED_LAYERS: scriptedLayersIDs.toString()
  };

  const projectionParams = (parseFloat(baseParams.VERSION) >= 1.3)
    ? {
      CRS: projection,
      I: Math.round(clickPosition[0]),
      J: Math.round(clickPosition[1])
    } : {
      SRS: projection,
      X: Math.round(clickPosition[0]),
      Y: Math.round(clickPosition[1])
    };

  return Object.assign(baseParams, projectionParams);
}

interface Modifiers {
  ctrlKey: boolean;
}

function selectFeatures (
  { url, selectedLayerId, data, mapSize, mapExtent, modifiers, mapScale, contentType }:
  { url: string; selectedLayerId: string; data: string; mapSize: DimensionDto; mapExtent: WorldBoxDto; modifiers: Modifiers; mapScale: number; contentType: string }) {
  const selectionUrlBase = url.replace(LAYER_ID, selectedLayerId);
  const queryParams = `mapScale=${mapScale}&mapSize=${mapSize}&mapExtent=${mapExtent}&ctrlKey=${modifiers.ctrlKey || supportsTouch}`;
  const selectionUrl = `${selectionUrlBase}${selectionUrlBase.includes('?') ? '&' : '?'}${queryParams}`;
  return Promise.resolve($.ajax(cadenzaUrl(selectionUrl), {
    method: 'POST',
    data,
    contentType
  }));
}
